import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SectionLabel } from "@/components/Reveal";

const STAGES = [
    {
        n: "01",
        title: "Blueprint",
        desc: "Sketches, models and light studies until the idea can stand on its own.",
    },
    {
        n: "02",
        title: "Planning",
        desc: "Permits, engineering and procurement sequenced before ground is broken.",
    },
    {
        n: "03",
        title: "Construction",
        desc: "Our own site teams build with tolerances measured in millimetres.",
    },
    {
        n: "04",
        title: "Interior",
        desc: "Stone, joinery and light composed room by room, object by object.",
    },
    {
        n: "05",
        title: "Delivery",
        desc: "A choreographed handover — keys, documentation and a decade of support.",
    },
];

export default function Process() {
    const ref = useRef(null);
    const trackRef = useRef(null);
    const [range, setRange] = useState(0);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end end"],
    });

    useEffect(() => {
        const measure = () => {
            if (trackRef.current)
                setRange(
                    Math.max(
                        0,
                        trackRef.current.scrollWidth - window.innerWidth,
                    ),
                );
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    const x = useTransform(scrollYProgress, [0, 1], [0, -range]);

    return (
        <section
            id="process"
            ref={ref}
            data-testid="process-section"
            className="relative h-[340vh] bg-onyx"
        >
            <div className="sticky top-0 flex h-screen flex-col justify-center overflow-clip">
                <div className="mx-auto w-full max-w-7xl px-6 sm:px-10">
                    <SectionLabel index="05" title="The Process" />
                </div>

                <motion.div
                    ref={trackRef}
                    style={{ x }}
                    className="mt-14 flex w-max items-stretch gap-6 pl-6 pr-[12vw] sm:pl-10"
                >
                    <div className="flex w-[78vw] flex-col justify-center sm:w-[38vw] lg:w-[30vw]">
                        <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                            From first line
                            <br />
                            <span className="text-outline">to final key.</span>
                        </h2>
                        <p className="mt-6 max-w-sm text-sm leading-relaxed text-ash">
                            One unbroken chain of custody. The people who draw
                            your building are the people who build it.
                        </p>
                    </div>

                    {STAGES.map((s, i) => (
                        <div
                            key={s.n}
                            data-testid={`process-stage-${s.n}`}
                            className="relative flex w-[78vw] flex-col justify-between rounded-2xl border border-white/[0.07] bg-graphite p-8 sm:w-[42vw] lg:w-[30vw]"
                        >
                            <div className="flex items-start justify-between">
                                <span className="font-display text-7xl font-black text-outline">
                                    {s.n}
                                </span>
                                <span className="mt-3 h-2 w-2 rotate-45 border border-gold/60" />
                            </div>
                            <div className="mt-24">
                                <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                                    {s.title}
                                </h3>
                                <p className="mt-3 max-w-xs text-sm leading-relaxed text-ash">
                                    {s.desc}
                                </p>
                            </div>
                            {i < STAGES.length - 1 && (
                                <span className="absolute -right-6 top-1/2 hidden h-px w-6 bg-gold/40 sm:block" />
                            )}
                        </div>
                    ))}
                </motion.div>

                <div className="mx-auto mt-14 w-full max-w-7xl px-6 sm:px-10">
                    <div className="h-px w-full bg-white/[0.08]">
                        <motion.div
                            className="h-px origin-left bg-gold"
                            style={{ scaleX: scrollYProgress }}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
